import { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Layers, BookOpen, GraduationCap, RotateCcw, Search, User, LogOut, Menu, X, ShieldCheck } from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "side-nav-dashboard" },
  { to: "/app/topics", label: "Question bank", icon: Layers, testid: "side-nav-topics" },
  { to: "/app/guides", label: "Revision guides", icon: BookOpen, testid: "side-nav-guides" },
  { to: "/app/flashcards", label: "Flashcards", icon: GraduationCap, testid: "side-nav-flashcards" },
  { to: "/app/review", label: "Review", icon: RotateCcw, testid: "side-nav-review" },
  { to: "/app/search", label: "Search", icon: Search, testid: "side-nav-search" },
  { to: "/app/account", label: "Account", icon: User, testid: "side-nav-account" },
];

export default function DashboardLayout() {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const links = (
    <nav className="flex flex-col gap-1">
      {NAV.map(({ to, label, icon: Icon, end, testid }) => (
        <NavLink key={to} to={to} end={end} data-testid={testid} onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
              isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
          <Icon className="w-4 h-4" /> {label}
        </NavLink>
      ))}
      {isAdmin && (
        <NavLink to="/admin" data-testid="side-nav-admin" onClick={() => setOpen(false)}
          className="flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
          <ShieldCheck className="w-4 h-4" /> Admin CMS
        </NavLink>
      )}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-layout">
      <header className="lg:hidden sticky top-0 z-40 bg-background border-b border-border h-14 flex items-center justify-between px-4">
        <Logo to="/app" />
        <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="p-2" aria-label="Toggle menu">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </header>
      {open && (
        <div className="lg:hidden fixed inset-0 z-30 bg-background pt-16 px-4" data-testid="mobile-menu">
          {links}
          <button data-testid="mobile-logout-btn" onClick={async () => { await logout(); navigate("/"); }}
            className="flex items-center gap-3 px-4 py-2.5 mt-4 text-sm font-medium text-destructive">
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      )}
      <div className="flex">
        <aside className="hidden lg:flex flex-col w-64 shrink-0 border-r border-border min-h-screen sticky top-0 p-5">
          <div className="mb-8"><Logo to="/app" /></div>
          {links}
          <div className="mt-auto pt-6 border-t border-border">
            <div className="px-4 py-2">
              <p className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
            <button data-testid="sidebar-logout-btn" onClick={async () => { await logout(); navigate("/"); }}
              className="flex items-center gap-3 px-4 py-2.5 w-full rounded-md text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
              <LogOut className="w-4 h-4" /> Log out
            </button>
            <Link to="/" data-testid="sidebar-home-link" className="block px-4 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
              ← Back to pamind.co.uk
            </Link>
          </div>
        </aside>
        <main className="flex-1 min-w-0 p-4 md:p-8 lg:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
